import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useId } from "react";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";
import type { TSubjectDetail } from "@/lib/types";
import { SubjectForm } from "./SubjectForm";

type ConflictReason = {
  subject: TSubjectDetail;
};

export function SubjectCreateDialog({
  accession,
  children,
  onCreated,
  onAlreadyExists,
  startingValue,
  ...props
}: {
  accession?: { id: number };
  children?: ReactNode;
  startingValue?: { value: string; type: "caseId" | "unosId" } | null;
  onCreated?: (subject: TSubjectDetail) => void;
  onAlreadyExists?: (subject: TSubjectDetail) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const formId = useId();

  const navigate = useNavigate();

  const create = trpc.subjects.create.useMutation({
    onSuccess: (data) => {
      props.onOpenChange?.(false);
      toast.success("Subject created");
      if (onCreated) {
        onCreated(data);
      } else {
        navigate({
          to: "/subjects/$id",
          params: { id: data.id },
        });
      }
    },
    onError: (err) => {
      if (err.data?.code === "CONFLICT" && err.shape?.reason) {
        const reason = err.shape.reason as ConflictReason;
        if (onAlreadyExists) {
          onAlreadyExists(reason.subject);
          return;
        }
        toast("Donor already exists", {
          action: {
            label: "View donor",
            onClick: () => {
              navigate({
                to: "/subjects/$id",
                params: { id: reason.subject.id },
              });
            },
          },
        });
      } else {
        toast.error("Error updating subject", {
          description: err.message,
        });
      }
    },
  });

  return (
    <DialogTrigger isOpen={props.open} onOpenChange={props.onOpenChange}>
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Create new donor</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <SubjectForm
              formId={formId}
              startingValue={startingValue}
              onSave={(values) => {
                create.mutate(values);
              }}
            />
          </DialogBody>
          <DialogFooter>
            <Button
              type="submit"
              form={formId}
              isPending={create.isPending}
              isDisabled={create.isPending}
            >
              Create
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
